import type { FC } from 'react';
import React, { useCallback, useState } from 'react';
import { MDText } from 'i18n-react';

import { BookmarkIcon, PlusIcon } from '@motorway/mw-highway-code';
import { Chip } from '@motorway/mw-highway-code/pre-alpha';

import {
	triggerAddSavedSearchButtonClickEvent,
	triggerUserClickedEmptySavedSearchPillEvent,
} from 'Services/analytics/events';
import { useSavedFilters } from 'Stores/SavedFiltersStore/SavedFiltersStore';

import texts from '../../../VehicleListV2.texts.json';

import { SavedSearchesFlow } from './SavedSearchesFlow';
import { useSavedSearchMatch } from './useSavedSearchMatch';
import { useSavedSearchStoreToast } from './useSavedSearchStoreToast';

import styles from './SavedSearchesChip.module.scss';

const LocalT = new MDText(texts);

const BookmarkOutlineIcon = () => (
	<div className={styles.iconOutline}>
		<BookmarkIcon filled={false} />
	</div>
);

const PlusOutlineIcon = () => (
	<div className={styles.iconOutline}>
		<PlusIcon />
	</div>
);

interface MobileChipProps {
	disabled?: boolean;
	isUniqueFilter: boolean;
	label?: string;
	onClick: () => void;
	selected: boolean;
}

const getMobileChipClassName = (label?: string, isUniqueFilter?: boolean) => {
	if (label) return styles.labelVariantMobile;
	if (isUniqueFilter) return styles.iconVariantSave;
	return styles.iconVariant;
};

const MobileChip: FC<MobileChipProps> = ({ disabled = false, isUniqueFilter, label, onClick, selected }) => (
	<span className={getMobileChipClassName(label, isUniqueFilter)}>
		<Chip
			reverse
			aria-label={`${LocalT.translate(isUniqueFilter ? 'filterBar.saveSearch' : 'filterBar.savedSearches')}`}
			data-testid="filter-chip-savedSearches-icon"
			disabled={disabled}
			icon={BookmarkOutlineIcon}
			label={label || (isUniqueFilter ? <PlusOutlineIcon /> : '')}
			onClick={onClick}
			selected={selected}
			size="medium"
		/>
	</span>
);

export const SavedSearchesChip: FC = () => {
	useSavedSearchStoreToast();
	const savedFilters = useSavedFilters();
	const { isUniqueFilter, matchedFilterId, matchedFilterName } = useSavedSearchMatch();

	const [isFlowOpen, setIsFlowOpen] = useState(false);
	const [flowInitialView, setFlowInitialView] = useState<'create' | 'list'>('list');

	const handleListClick = useCallback(() => {
		triggerUserClickedEmptySavedSearchPillEvent();
		setFlowInitialView('list');
		setIsFlowOpen(true);
	}, []);

	const handleSaveClick = useCallback(() => {
		triggerAddSavedSearchButtonClickEvent();
		setFlowInitialView('create');
		setIsFlowOpen(true);
	}, []);

	if (matchedFilterId && matchedFilterName) {
		return (
			<>
				<span className={styles.labelVariant}>
					<Chip
						reverse
						selected
						className={styles.chip}
						data-testid="filter-chip-savedSearches"
						icon={BookmarkOutlineIcon}
						label={matchedFilterName}
						onClick={handleListClick}
						size="medium"
					/>
				</span>
				<MobileChip selected isUniqueFilter={false} label={matchedFilterName} onClick={handleListClick} />
				<SavedSearchesFlow activeFilterId={matchedFilterId} isOpen={isFlowOpen} onClose={() => setIsFlowOpen(false)} />
			</>
		);
	}

	if (isUniqueFilter) {
		return (
			<>
				<span className={styles.labelVariant}>
					<Chip
						reverse
						className={styles.chip}
						data-testid="filter-chip-savedSearches"
						icon={PlusOutlineIcon}
						label={LocalT.translate('filterBar.saveSearch')}
						onClick={handleSaveClick}
						size="medium"
					/>
				</span>
				<MobileChip isUniqueFilter onClick={handleSaveClick} selected={false} />
				<SavedSearchesFlow initialView={flowInitialView} isOpen={isFlowOpen} onClose={() => setIsFlowOpen(false)} />
			</>
		);
	}

	return (
		<>
			<span className={styles.labelVariant}>
				<Chip
					reverse
					className={styles.chip}
					data-testid="filter-chip-savedSearches"
					disabled={savedFilters.length === 0}
					icon={BookmarkOutlineIcon}
					label={LocalT.translate('filterBar.savedSearches')}
					onClick={handleListClick}
					size="medium"
				/>
			</span>
			<MobileChip
				disabled={savedFilters.length === 0}
				isUniqueFilter={false}
				onClick={handleListClick}
				selected={false}
			/>
			<SavedSearchesFlow activeFilterId={matchedFilterId} isOpen={isFlowOpen} onClose={() => setIsFlowOpen(false)} />
		</>
	);
};
